import {
  CalendarToday as CalendarTodayIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { uniq } from 'lodash';
import {
  type ChangeEvent,
  type FocusEvent,
  type MouseEvent,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import AssemblyIndoorImage from 'src/assets/images/AssemblyIndoor.svg?react';
import AssemblyMacCafeImage from 'src/assets/images/AssemblyMacCafe.svg?react';
import ChannelOutdoorImage from 'src/assets/images/ChannelOutdoor.svg?react';
import { FilterDropdownWithSearch } from 'src/components/filterDropdownWithSearch/FilterDropdownWithSearch';
import { Footer } from 'src/components/footer/Footer';
import { Progress } from 'src/components/progress/Progress';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { ISO_DATE_FORMAT } from 'src/constants';
import { getGcfComponentConfig } from 'src/constants/gcfComponentConfig';
import { PATHS } from 'src/constants/paths';
import {
  useCreateComponent,
  useGetProjectConfig,
  useGetThemes,
} from 'src/hooks/api';
import { ComponentTypes, IStatus } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';
import {
  getComponentDisplayName,
  hideCODAndThankYouByLayoutType,
  isComponentEnabled,
} from 'src/utils/component-utils';
import { createComponentEvent } from 'src/utils/google-tag-manager';

import { InputTextLabel } from '../../../components/input/Input.styled';
import { Snackbar } from '../../../components/snackbar';
import {
  CreateComponentStepEnums,
  SelectedOptions,
} from './AddComponentPage.interface';
import {
  CheckBoxContainer,
  Container,
  FilterDropDownWithBadgesContainer,
  FolderOpenIconStyled,
  FormWrapper,
  IconContainer,
  ImageButton,
  InputDate,
  InputLabel,
  InputTextField,
  LayoutTypeWrapper,
  MacCafeIconContainer,
  MainOOContainer,
  OOSubText,
  OptionWrapper,
  OutdoorIconContainer,
  OwnerOperatorContainer,
  SoftSellContainer,
  Step,
  StepContent,
  Wrapper,
} from './AddComponentPage.styled';
import { TestIds } from './constants';
import { useAddComponentPage } from './hooks/AddComponentPage.hook';

const initialSelectedOptions: Partial<SelectedOptions> = {
  componentName: '',
  expiryDate: null,
  startDate: null,
  ownerOperatorPermission: false,
};

export const AddComponentPage = () => {
  const createNewComponent = useCreateComponent();
  const { isViewOnly } = useUserRole();
  const navigate = useNavigate();
  const {
    isLoadingLayoutTypes,
    isLoadingComponentTypes,
    isLoadingSections,
    errorLoadingLayoutTypes,
    errorLoadingComponentTypes,
    errorLoadingSections,
    layoutTypes,
    componentTypes,
    layoutTypeState,
    componentTypeState,
    sections,
    setLayoutType,
    setComponentType,
  } = useAddComponentPage();
  const { data: projectConfig } = useGetProjectConfig();
  const { data: themes } = useGetThemes();

  const [activeStep, setActiveStep] = useState(
    CreateComponentStepEnums.SELECT_LAYOUT_TYPE,
  );
  const [categoriesAdded, setCategoriesAdded] = useState<string[]>([]);
  const [componentTagsAdded, setComponentTagsAdded] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<
    Partial<SelectedOptions>
  >(initialSelectedOptions);

  const layoutThemeMap = {
    [lang.indoor]: projectConfig?.indoorDefaultTheme,
    [lang.outdoor]: projectConfig?.outdoorDefaultTheme,
    [lang.mcCafe]: projectConfig?.mcCafeDefaultTheme,
    [lang.digitalMerchandiser]: projectConfig?.digitalMerchandiserDefaultTheme,
  };

  const layoutName = selectedOptions.layoutType?.name;
  const defaultThemeName =
    layoutThemeMap[layoutName as keyof typeof layoutThemeMap];

  const defaultTheme = themes?.find((theme) => theme.name === defaultThemeName);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    const newActiveStep = activeStep - 1;

    switch (newActiveStep) {
      case CreateComponentStepEnums.SELECT_LAYOUT_TYPE:
        setComponentType(0);
        setSelectedOptions((prev) => ({
          ...initialSelectedOptions,
          layoutType: {
            ...prev.layoutType!,
          },
        }));
        break;
      case CreateComponentStepEnums.SELECT_COMPONENT_TYPE:
        setSelectedOptions((prev) => ({
          ...initialSelectedOptions,
          layoutType: {
            ...prev.layoutType!,
          },
          componentType: {
            ...prev.componentType!,
          },
        }));
        break;
      case CreateComponentStepEnums.SELECT_SIZE:
        setSelectedOptions((prev) => ({
          ...initialSelectedOptions,
          layoutType: {
            ...prev.layoutType!,
          },
          componentType: {
            ...prev.componentType!,
          },
          sectionSize: {
            ...prev.sectionSize!,
          },
        }));
        break;
      case CreateComponentStepEnums.SELECT_OWNER_OPERATOR:
        setSelectedOptions((prev) => ({
          ...initialSelectedOptions,
          layoutType: {
            ...prev.layoutType!,
          },
          componentType: {
            ...prev.componentType!,
          },
          sectionSize: {
            ...prev.sectionSize!,
          },
          ownerOperatorPermission: prev.ownerOperatorPermission,
          startDate: prev.startDate,
        }));
        break;
    }

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCancel = () => {
    navigate(PATHS.COMPONENTS);
  };

  const handleClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    setSelectedOptions((prevSelectedOptions) => ({
      ...prevSelectedOptions,
      expiryDate: null,
    }));
  };

  const handleClearStartDate = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    setSelectedOptions((prevSelectedOptions) => ({
      ...prevSelectedOptions,
      startDate: null,
    }));
  };

  const handleDone = async () => {
    if (
      selectedOptions.componentName &&
      selectedOptions.componentType?.id &&
      selectedOptions.sectionSize?.id
    ) {
      let formattedDate = '';
      let formattedStartDate = '';

      if (selectedOptions.expiryDate) {
        formattedDate = dayjs(selectedOptions.expiryDate).format(
          ISO_DATE_FORMAT,
        );
      }
      if (selectedOptions.startDate) {
        formattedStartDate = dayjs(selectedOptions.startDate).format(
          ISO_DATE_FORMAT,
        );
      }
      const componentConfigDefaults = getGcfComponentConfig(
        selectedOptions.componentType?.name,
      );
      const request = {
        name: selectedOptions.componentName,
        component_type_id: selectedOptions.componentType?.id,
        layout_type_id: selectedOptions.layoutType?.id,
        end_date: formattedDate ? formattedDate : undefined,
        start_date: formattedStartDate ? formattedStartDate : undefined,
        width: selectedOptions.sectionSize.width,
        height: selectedOptions.sectionSize.height,
        status: IStatus.DRAFT,
        usable_as_override: selectedOptions.ownerOperatorPermission,
        ...(componentConfigDefaults && {
          config: {
            ...componentConfigDefaults,
          },
        }),
        theme_id: defaultTheme?.id,
        product_category_id: uniq(categoriesAdded).map(Number),
        component_tag_id: uniq(componentTagsAdded).map(Number),
      };
      const result = await createNewComponent.mutateAsync(request);
      createComponentEvent(
        result.id,
        selectedOptions.componentType.name,
        selectedOptions.componentName,
        selectedOptions.layoutType?.name!,
      );

      navigate(PATHS.COMPONENT_EDIT(result.id));

      Snackbar.success(
        `${selectedOptions.componentName} ${lang.successfullyCreated}`,
      );
    }
  };

  const handleSelect = (key: string, value: any) => {
    setSelectedOptions((prevSelectedOptions) => ({
      ...prevSelectedOptions,
      [key]: value,
    }));
  };

  if (
    errorLoadingLayoutTypes ||
    errorLoadingComponentTypes ||
    errorLoadingSections
  )
    // TODO: Replace with error component
    return <p>{lang.error}</p>;

  const onCheckboxClick = () => {
    setSelectedOptions((prevSelectedOptions) => ({
      ...prevSelectedOptions,
      startDate: null,
      ownerOperatorPermission: !selectedOptions.ownerOperatorPermission,
    }));
  };

  const disablePastDates = (date: dayjs.Dayjs) => {
    return date.isBefore(dayjs().startOf('day').add(1, 'day'));
  };

  const steps = [
    {
      title: lang.createComponentTitle,
      isLoading: isLoadingLayoutTypes,
      content: (
        <LayoutTypeWrapper>
          {layoutTypes?.map((layoutType, index) => {
            return (
              <ImageButton
                key={index}
                selected={selectedOptions?.layoutType?.id === layoutType.id}
                onClick={() => {
                  if (layoutTypeState !== layoutType.id) {
                    setLayoutType(layoutType.id);
                    handleSelect('layoutType', layoutType);
                  }
                }}>
                {(() => {
                  switch (layoutType.name) {
                    case 'Indoor':
                      return (
                        <IconContainer>
                          <AssemblyIndoorImage />
                        </IconContainer>
                      );
                    case 'Outdoor':
                      return (
                        <OutdoorIconContainer>
                          <ChannelOutdoorImage />
                        </OutdoorIconContainer>
                      );
                    default:
                      return (
                        <MacCafeIconContainer>
                          <AssemblyMacCafeImage />
                        </MacCafeIconContainer>
                      );
                  }
                })()}
                {layoutType.name}
              </ImageButton>
            );
          })}
        </LayoutTypeWrapper>
      ),
      footer: (
        <Footer
          showFooterActions
          showArrow
          saveLabel={lang.next}
          cancelLabel={lang.cancel}
          disableSave={!selectedOptions.layoutType?.id}
          onSave={handleNext}
          onCancel={handleCancel}
        />
      ),
    },
    {
      title: lang.selectComponentType,
      isLoading: isLoadingComponentTypes,
      content: (
        <OptionWrapper>
          {componentTypes.map((componentType) => (
            <ImageButton
              key={componentType.id}
              selected={selectedOptions.componentType?.id === componentType.id}
              onClick={() => {
                if (componentTypeState !== componentType.id) {
                  setComponentType(componentType.id);
                  handleSelect('componentType', componentType);
                }
              }}
              disabled={
                !isComponentEnabled(componentType.name) ||
                hideCODAndThankYouByLayoutType(
                  componentType.name,
                  selectedOptions.layoutType?.name,
                )
              }>
              {getComponentDisplayName(componentType.name)}
            </ImageButton>
          ))}
        </OptionWrapper>
      ),
      footer: (
        <Footer
          showFooterActions
          showArrow
          saveLabel={lang.next}
          cancelLabel={lang.cancel}
          disableSave={!selectedOptions.componentType?.id}
          onSave={handleNext}
          onBack={handleBack}
          onCancel={handleCancel}
        />
      ),
    },
    {
      title: lang.selectSectionSize,
      isLoading: isLoadingSections,
      content: (
        <OptionWrapper>
          {sections.map((section: any) => (
            <ImageButton
              key={section.id}
              selected={selectedOptions.sectionSize?.id === section.id}
              onClick={() => handleSelect('sectionSize', section)}>
              {`${section.name} ${section.width}x${section.height}`}
            </ImageButton>
          ))}
        </OptionWrapper>
      ),
      footer: (
        <Footer
          showFooterActions
          showArrow
          saveLabel={lang.next}
          cancelLabel={lang.cancel}
          disableSave={!selectedOptions.sectionSize?.id}
          onSave={handleNext}
          onBack={handleBack}
          onCancel={handleCancel}
        />
      ),
    },
    {
      title: lang.ownerOperator.ownerOperatorHeader,
      content: (
        <MainOOContainer>
          <FolderOpenIconStyled />
          <Container>
            <OwnerOperatorContainer>
              <CheckBoxContainer>
                <Checkbox
                  checked={selectedOptions.ownerOperatorPermission}
                  onClick={onCheckboxClick}
                />
                <OOSubText>
                  {lang.ownerOperator.allowOwnerOperatorText}
                </OOSubText>
              </CheckBoxContainer>
            </OwnerOperatorContainer>
            {selectedOptions.componentType?.name ===
              ComponentTypes.PROMOTIONAL_SPACE && (
              <SoftSellContainer>
                <InputLabel
                  $disabled={selectedOptions.ownerOperatorPermission === false}>
                  {lang.softSellDate}
                </InputLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <InputDate
                    shouldDisableDate={disablePastDates}
                    format="MM-DD-YYYY"
                    inputFormat="MM-DD-YYYY"
                    ampm={false}
                    value={
                      selectedOptions.startDate
                        ? dayjs(selectedOptions.startDate)
                        : null
                    }
                    maxDate={
                      selectedOptions.expiryDate
                        ? dayjs(selectedOptions.expiryDate).subtract(1, 'day')
                        : undefined
                    }
                    disabled={selectedOptions.ownerOperatorPermission === false}
                    onChange={(value: dayjs.Dayjs | null) => {
                      handleSelect(
                        'startDate',
                        value && value.isValid()
                          ? value.format('YYYY-MM-DD')
                          : null,
                      );
                    }}
                    slotProps={{
                      textField: {
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              {selectedOptions.startDate && (
                                <IconButton onClick={handleClearStartDate}>
                                  <CancelIcon />
                                </IconButton>
                              )}
                              <CalendarTodayIcon />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </SoftSellContainer>
            )}
          </Container>
        </MainOOContainer>
      ),
      footer: (
        <Footer
          showFooterActions
          showArrow
          saveLabel={lang.next}
          cancelLabel={lang.cancel}
          disableSave={false}
          onSave={handleNext}
          onBack={handleBack}
          onCancel={handleCancel}
        />
      ),
    },
    {
      title: lang.finalizeSettings,
      content: (
        <FormWrapper>
          <div>
            <InputTextLabel>{lang.layoutType}</InputTextLabel>
            <InputTextField
              helperText=" "
              variant="standard"
              value={selectedOptions?.layoutType?.name}
              InputProps={{
                readOnly: true,
              }}
            />
          </div>
          <div>
            <InputTextLabel>{lang.componentType}</InputTextLabel>
            <InputTextField
              helperText=" "
              variant="standard"
              value={getComponentDisplayName(
                selectedOptions?.componentType?.name as ComponentTypes,
              )}
              InputProps={{
                readOnly: true,
              }}
            />
          </div>
          <div>
            <InputTextLabel>{lang.size}</InputTextLabel>
            <InputTextField
              helperText=" "
              variant="standard"
              value={
                selectedOptions.sectionSize
                  ? `${selectedOptions.sectionSize.name} ${selectedOptions.sectionSize.width}x${selectedOptions.sectionSize.height}`
                  : ''
              }
              InputProps={{
                readOnly: true,
              }}
            />
          </div>
          <div>
            <InputTextLabel>{`${lang.componentName} *`}</InputTextLabel>
            <InputTextField
              helperText=" "
              variant="filled"
              value={selectedOptions.componentName}
              InputLabelProps={{ shrink: true }}
              onBlur={(event: FocusEvent<HTMLInputElement>) =>
                handleSelect('componentName', event.target.value.trim())
              }
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleSelect('componentName', event.target.value)
              }
              inputProps={{ 'data-testid': TestIds.COMPONENT_NAME_INPUT }}
            />
          </div>
          {![
            ComponentTypes.COD,
            ComponentTypes.COD_THANK_YOU,
            ComponentTypes.DISCLAIMER,
          ].includes(selectedOptions.componentType?.name as ComponentTypes) && (
            <FilterDropDownWithBadgesContainer>
              <FilterDropdownWithSearch
                name={lang.restrictCategoryText}
                label={lang.restrictCategoryText}
                placeholder={lang.addCategoryText}
                multiline={true}
                onChange={(value: any) => {
                  setCategoriesAdded(value);
                }}
                value={categoriesAdded}
                badgeOptions={{
                  align: 'top',
                }}
              />
            </FilterDropDownWithBadgesContainer>
          )}
          <div>
            <FilterDropDownWithBadgesContainer>
              <FilterDropdownWithSearch
                name={lang.componentTag}
                label={lang.componentTag}
                placeholder={lang.addComponentTag}
                multiline={true}
                onChange={(value: any) => {
                  setComponentTagsAdded(value);
                }}
                value={componentTagsAdded}
                badgeOptions={{
                  align: 'bottom',
                }}
              />
            </FilterDropDownWithBadgesContainer>
          </div>

          <div>
            <InputTextLabel>{lang.expiryDate}</InputTextLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <InputDate
                shouldDisableDate={disablePastDates}
                format="MM-DD-YYYY"
                inputFormat="MM-DD-YYYY"
                ampm={false}
                value={
                  selectedOptions.expiryDate
                    ? dayjs(selectedOptions.expiryDate)
                    : null
                }
                minDate={
                  selectedOptions.startDate
                    ? dayjs(selectedOptions.startDate).add(1, 'day')
                    : undefined
                }
                onChange={(value: dayjs.Dayjs | null) => {
                  handleSelect(
                    'expiryDate',
                    value && value.isValid()
                      ? value.format('YYYY-MM-DD')
                      : null,
                  );
                }}
                slotProps={{
                  textField: {
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {selectedOptions.expiryDate && (
                            <IconButton onClick={handleClear}>
                              <CancelIcon />
                            </IconButton>
                          )}
                          <CalendarTodayIcon />
                        </InputAdornment>
                      ),
                    },
                    inputProps: {
                      'data-testid': TestIds.EXPIRY_DATE,
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        </FormWrapper>
      ),
      footer: (
        <Footer
          showFooterActions
          showArrow
          saveLabel={lang.beginCreating}
          cancelLabel={lang.cancel}
          disableSave={!selectedOptions.componentName}
          onSave={handleDone}
          onBack={handleBack}
          onCancel={handleCancel}
        />
      ),
    },
  ];

  return (
    <Wrapper>
      <Box sx={{ position: 'absolute', top: 0, right: 0, margin: 3 }}>
        {isViewOnly && <ViewOnlyAlert />}
      </Box>
      <Step sx={{ maxWidth: 'lg' }}>
        <Typography variant="h1" gutterBottom>
          {steps[activeStep].title}
        </Typography>
        <StepContent>
          {steps[activeStep].isLoading ? (
            <Progress />
          ) : (
            steps[activeStep].content
          )}
        </StepContent>

        {!isViewOnly && steps[activeStep].footer}
      </Step>
    </Wrapper>
  );
};
